import React from 'react';
import { Table, Badge, Button, Avatar } from '@/components/ui';
import type { MaintenanceLog, LogStatus } from '../types/maintenance-logs.types';

export interface LogTableProps {
  data: MaintenanceLog[];
  isLoading: boolean;
  onEdit: (item: MaintenanceLog) => void;
  onRowClick: (item: MaintenanceLog) => void;
}

const statusColors: Record<LogStatus, string> = {
  draft: 'bg-slate-100 text-slate-500',
  submitted: 'bg-blue-100 text-blue-600',
  approved: 'bg-emerald-100 text-emerald-600',
  rejected: 'bg-red-100 text-red-600',
};

const statusLabels: Record<LogStatus, string> = {
  draft: 'Bản nháp',
  submitted: 'Chờ duyệt',
  approved: 'Đã duyệt',
  rejected: 'Từ chối',
};

export function LogTable({ data, isLoading, onEdit, onRowClick }: LogTableProps) {
  return (
    <Table
      columns={[
        { key: 'date', title: 'Ngày thực hiện' },
        { key: 'task', title: 'Công việc' },
        { key: 'worker', title: 'Nhân sự' },
        { key: 'quantity', title: 'Khối lượng', align: 'center' },
        { key: 'status', title: 'Trạng thái', align: 'center' },
        { key: 'actions', title: '', align: 'right' },
      ]}
      data={data}
      isLoading={isLoading}
      onRowClick={onRowClick}
      renderCell={(item, key) => {
        switch (key) {
          case 'date':
            return (
              <div className="flex flex-col">
                <span className="text-xs font-bold text-slate-700">{new Date(item.log_date).toLocaleDateString('vi-VN')}</span>
                <span className="text-[10px] text-slate-400 font-bold">{item.start_time || '--:--'} - {item.end_time || '--:--'}</span>
              </div>
            );
          case 'task':
            return (
              <div className="flex flex-col">
                <span className="font-bold text-slate-900">{item.task_name}</span>
                <div className="flex items-center gap-1 mt-0.5">
                  <span className="text-[9px] text-slate-400 font-bold  tracking-tighter">{item.task_type}</span>
                  <span className="text-slate-300">•</span>
                  <span className="text-[10px] text-os-primary font-bold">{item.zone?.zone_name || 'Toàn dự án'}</span>
                </div>
              </div>
            );
          case 'worker':
            return (
              <div className="flex items-center gap-2">
                <Avatar src={item.worker?.avatar_url} name={item.worker?.full_name} size="xs" />
                <div className="flex flex-col">
                  <span className="text-xs font-bold text-slate-700">{item.worker?.full_name}</span>
                  <span className="text-[9px] text-slate-400 font-bold ">{item.team?.name || '---'}</span>
                </div>
              </div>
            );
          case 'quantity':
            return (
              <div className="flex flex-col items-center">
                <span className="text-xs font-bold text-os-primary">{item.quantity_actual}</span>
                <span className="text-[9px] text-slate-400  font-bold">{item.unit}</span>
              </div>
            );
          case 'status':
            return (
              <Badge className={`${statusColors[item.status_code]} border-transparent text-[10px] font-bold `}>
                {statusLabels[item.status_code]}
              </Badge>
            );
          case 'actions':
            return (
              <div className="flex justify-end gap-1">
                <Button variant="ghost" size="sm" icon="ri-edit-line" onClick={(e) => { e.stopPropagation(); onEdit(item); }} />
              </div>
            );
          default:
            return null;
        }
      }}
    />
  );
}
